import React, { useState, useEffect } from 'react';
import Header from '../Header';
import { vendorAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { notify, confirm } from '../../utils/notify';
import { useListControls, ListToolbar, Pagination } from '../../components/ListControls';
import Topbar from "../../components/Topbar";

const STAGE_INFO = {
  PENDING_RM: { label: 'Pending Reporting Manager', role: 'manager' },
  PENDING_HOD: { label: 'Pending HOD Approval', role: 'hod' },
};

function ApprovalDots({ status }) {
  const steps = [
    { l: 'Reporting\nManager', done: ['PENDING_HOD', 'APPROVED'].includes(status), active: status === 'PENDING_RM' },
    { l: 'HOD', done: status === 'APPROVED', active: status === 'PENDING_HOD' },
    { l: 'Vendor Code\nGenerated', done: status === 'APPROVED', active: false },
  ];
  return (
    <div className="d-flex align-items-center mt-2">
      {steps.map((s, i) => (
        <div key={i} className="d-flex align-items-center" style={{ flex: 1 }}>
          <div className="d-flex flex-column align-items-center">
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: s.done ? '#22c55e' : s.active ? '#4f6ef7' : '#f3f4f6', color: s.done || s.active ? '#fff' : '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.552rem', fontWeight: 700 }}>
              {s.done ? <i className="bi bi-check" /> : i + 1}
            </div>
            <div style={{ fontSize: '0.46rem', color: s.done || s.active ? '#374151' : '#aaa', textAlign: 'center', marginTop: 2, width: 43, whiteSpace: 'pre-line', lineHeight: 1.3 }}>{s.l}</div>
          </div>
          {i < 2 && <div style={{ flex: 1, height: 2, background: s.done ? '#22c55e' : '#e5e7eb', marginBottom: 8 }} />}
        </div>
      ))}
    </div>
  );
}

function VendorApprovals() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    vendorAPI.listVendors({ myApprovals: 'true' })
      .then(d => setVendors(d.vendors || []))
      .catch(e => notify.error(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const canApprove = (v) => {
    if (!user) return false;
    // Server decides (assigned RM / HOD, their delegate, or admin) — any role
    if (typeof v.canAct === 'boolean') return v.canAct;
    if (user.role === 'admin') return true;
    const info = STAGE_INFO[v.approvalStatus];
    if (!info) return false;
    if (v.approvalStatus === 'PENDING_RM') return String(v.assignedRMId?._id || v.assignedRMId) === String(user.id);
    if (v.approvalStatus === 'PENDING_HOD') return String(v.assignedHODId?._id || v.assignedHODId) === String(user.id);
    return false;
  };

  const approve = async (v) => {
    const nextLabel = v.approvalStatus === 'PENDING_RM' ? 'Sends to HOD for final approval.' : 'This will generate the Vendor Code and activate the vendor.';
    const res = await confirm.action(`Approve vendor "${v.name}"?`, nextLabel, 'Yes, Approve', '#16a34a');
    if (!res.isConfirmed) return;
    setActing(v._id);
    try {
      await vendorAPI.approveVendor(v._id, { remarks: 'Approved' });
      notify.success('Vendor approved!');
      await load();
    } catch (e) { notify.error(e.message || 'Failed'); } finally { setActing(null); }
  };

  const reject = async (v) => {
    const res = await confirm.reject(`Reject vendor registration for "${v.name}"?`);
    if (!res.isConfirmed) return;
    setActing(v._id);
    try {
      await vendorAPI.rejectVendor(v._id, { remarks: res.value });
      notify.success('Vendor registration rejected');
      await load();
    } catch (e) { notify.error(e.message || 'Failed'); } finally { setActing(null); }
  };

  const lc = useListControls(vendors, {
    searchFields: ['name', 'pan', 'gst', v=>v.createdBy?.userName],
    dateField: 'createdAt',
    pageSize: 10,
  });

  return (
    <>
      <Header />
      <div id="db-wrapper"><main id="page-content">
          <Topbar section="Vendor Payment" /><section className="container-fluid p-4 pt-3">
        <div className="page-hero d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div><h5 className="text-white lh-1">Vendor Registration Approvals</h5><div className="sub">User → Reporting Manager → HOD approval flow. Vendor Code is generated only after HOD approval.</div></div>
          <button onClick={load} className="btn fw-semibold" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', padding: '6px 13px' }}>
            <i className="bi bi-arrow-clockwise me-1" />Refresh
          </button>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: 'Pending RM Review', val: vendors.filter(v => v.approvalStatus === 'PENDING_RM').length, bg: '#fffbeb', ic: '#f59e0b' },
            { label: 'Pending HOD Approval', val: vendors.filter(v => v.approvalStatus === 'PENDING_HOD').length, bg: '#f3e8ff', ic: '#7c3aed' },
            { label: 'Total Pending', val: vendors.length, bg: '#eff6ff', ic: '#3b82f6' },
          ].map((s, i) => (
            <div key={i} className="col-4">
              <div className="kpi-card d-flex align-items-center gap-2">
                <div className="kpi-icon" style={{ background: s.bg }}><i className="bi bi-hourglass" style={{ color: s.ic }} /></div>
                <div><div style={{ fontSize: '1.17rem', fontWeight: 700, lineHeight: 1 }}>{s.val}</div><div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>{s.label}</div></div>
              </div>
            </div>
          ))}
        </div>

        <ListToolbar search={lc.search} setSearch={lc.setSearch} dateFrom={lc.dateFrom} setDateFrom={lc.setDateFrom} dateTo={lc.dateTo} setDateTo={lc.setDateTo} placeholder="Search by vendor name, PAN, GST..." dateLabel="Registered"/>

        {loading ? <div className="text-center py-5"><span className="spinner-border text-primary" /></div>
          : lc.paginated.length === 0 ? <div className="empty-state"><i className="bi bi-inbox" /><p>No vendor registrations pending your approval</p></div>
          : lc.paginated.map((v, idx) => {
            const info = STAGE_INFO[v.approvalStatus] || {};
            return (
              <div key={v._id} className="record-card">
                <div className="record-number">{(lc.page-1)*lc.pageSize+idx + 1}.</div>
                <div className="record-body">
                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <div style={{ fontWeight: 700, fontSize: '0.874rem', color: '#111827' }}>{v.name}</div>
                    <span className="status-pill status-pending"><i className="bi bi-hourglass" />{info.label || v.approvalStatus}</span>
                    <span style={{ background: '#f3f4f6', color: '#6b7280', fontSize: '0.7rem', padding: '2px 6px', borderRadius: 12 }}>{v.category}</span>
                  </div>
                  <div className="row g-2">
                    <div className="col-12 col-md-5">
                      <div className="record-field"><span className="rf-label">PAN — </span><span className="rf-value">{v.pan}</span></div>
                      <div className="record-field"><span className="rf-label">GST — </span><span className="rf-value">{v.gst || '—'}</span></div>
                      <div className="record-field"><span className="rf-label">Bank — </span><span className="rf-value">{v.bankName || '—'} {v.accountNumber ? `· ${v.accountNumber}` : ''}</span></div>
                      <div className="record-field"><span className="rf-label">Registered By — </span><span className="rf-value">{v.createdBy?.userName || '—'}</span></div>
                    </div>
                    <div className="col-12 col-md-7">
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>APPROVAL PROGRESS</div>
                      <ApprovalDots status={v.approvalStatus} />
                      {v.approvalHistory?.length > 0 && (
                        <div className="mt-2 d-flex gap-1 flex-wrap">
                          {v.approvalHistory.map((h, i) => (
                            <span key={i} style={{ fontSize: '0.598rem', padding: '1px 5px', borderRadius: 12, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>
                              {h.stage} <i className="bi bi-check" />{h.approverId?.userName || 'Approved'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {canApprove(v) && <><div className="divider d-none d-md-block" />
                  <div className="record-actions">
                    <button className="btn-card-approve" onClick={() => approve(v)} disabled={acting === v._id}>
                      {acting === v._id ? <span className="spinner-border spinner-border-sm" /> : 'Approve'}
                    </button>
                    <div className="btn-divider" />
                    <button className="btn-card-reject" onClick={() => reject(v)} disabled={acting === v._id}>Reject</button>
                  </div></>}
              </div>
            );
          })}
        <Pagination page={lc.page} totalPages={lc.totalPages} setPage={lc.setPage} totalCount={lc.totalCount} pageSize={lc.pageSize} itemLabel="vendors"/>
      </section></main></div>
    </>
  );
}
export default VendorApprovals;
